import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ProfileDto } from 'src/app/shared/dto/profile.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { CustomErrorStatusesEnum } from 'src/app/shared/enums/custom-error-statuses.enum';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';
import { environment } from 'src/environments/environment';
import { UserActivityHistoryDto } from 'src/app/shared/dto/user-activity-history.dto';
import { UserActivityHistoryResponseDto } from 'src/app/shared/dto/pagination/users-activity-history-response.dto';

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService {
    private usersSubject = new BehaviorSubject<UserDto[]>([]);
    public users$ = this.usersSubject.asObservable();

    private readonly uls = inject(UserLoggedService);
    private readonly http = inject(HttpClient);
    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'users');
    }

    async setupWebSocket(): Promise<void> {
        try {
            this.usersSubject.next((await this.getUsers()));
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.USERS_ROOM));
            this.socketService.getNotifications(Messages.USERS_CHANGED).subscribe(async () => {
                const response = await this.getUsers();
                this.usersSubject.next(response);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Usuarios. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.USERS_ROOM));
    }

    async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
        const userLogged: UserDto = this.uls.userLogged;

        const request = this.http.post<any>(
            `${environment.apiUrl}users/updatePassword/`,
            {
                id: userLogged.id,
                oldPassword: oldPassword,
                newPassword: newPassword
            }
        );

        const updatedUser: UserDto = await lastValueFrom(request);

        return updatedUser !== null && updatedUser !== undefined;
    }

    async getUsers(): Promise<UserDto[]> {
        try {
            const request = this.http.get<UserDto[]>(`${environment.apiUrl}users`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los usuarios. Error: ${e.message}`);
        }
    }

    async createUser(user: UserDto): Promise<UserDto> {
        try {
            if (user === null || user === undefined) {
                throw new Error('El usuario no es válido');
            }

            const request = this.http.post<UserDto>(`${environment.apiUrl}users`, user);

            return await lastValueFrom(request);
        } catch (e: any) {
            if (e.status === CustomErrorStatusesEnum.USERS_LIMIT_LICENSE) {
                throw new Error('Tu licencia no permite crear más usuarios.');
            }

            throw new Error(`No se ha podido crear el usuario. Error: ${e.message}`);
        }
    }

    async updateUser(user: UserDto): Promise<UserDto> {
        try {
            if (user === null || user === undefined) {
                throw new Error('El usuario no es válido');
            }

            const request = this.http.patch<UserDto>(`${environment.apiUrl}users/${user.id}`, user);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar el usuario. Error: ${e.message}`);
        }
    }

    async deleteUser(id: number): Promise<UserDto> {
        try {
            if (id <= 0) {
                throw new Error('El id del usuario ha de ser mayor de 0');
            }

            const request = this.http.delete<UserDto>(`${environment.apiUrl}users/${id}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido eliminar el usuario. Error: ${e.message}`);
        }
    }

    async getProfiles(): Promise<ProfileDto[]> {
        try {
            const request = this.http.get<ProfileDto[]>(`${environment.apiUrl}profiles`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los perfiles. Error: ${e.message}`);
        }
    }

    async getUsersHistory(userName?: string, startDate?: Date, endDate?: Date, page: number = 1, pageSize: number = 10): Promise<UserActivityHistoryResponseDto> {
        try {
            const params: any = {};

            if (userName) {
                params.userName = userName;
            }

            if (startDate) {
                params.startDate = startDate;
            }

            if(endDate) {
                params.endDate = endDate;
            }

            params.page = page;
            params.pageSize = pageSize;

            const request = this.http.get<UserActivityHistoryResponseDto>(`${environment.apiUrl}users/history`, { params });

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el historial de usuarios. Error: ${e.message}`);
        }
    }

    async getUsersHistoryByOrderProcessId(orderProcessId: number): Promise<UserActivityHistoryDto[]> {
        try {
            if(orderProcessId === null || orderProcessId === undefined || orderProcessId <= 0) {
                throw new Error('El id del proceso no es válido');
            }
            const request = this.http.get<UserActivityHistoryDto[]>(`${environment.apiUrl}users/history/${orderProcessId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el historial de usuarios. Error: ${e.message}`);
        }
    }

   async getAssignableProfiles(): Promise<ProfileDto[]> {
    try {
        return await lastValueFrom(
            this.http.get<ProfileDto[]>(`${environment.apiUrl}profiles/assignable`)
        );
    } catch (e: any) {
        throw new Error(`Error al cargar perfiles asignables. Error: ${e.message}`);
    }
}
}
